/** /activity-categories — cross-tenant Activity_Category catalog (grid).
 *
 * List rows carry the icon chunks; the Image column shows a small "has icon"
 * marker (the actual icon renders on the detail page). The Activities count
 * opens the activities grid filtered to that category (and its operator). */
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { Box, Button, CircularProgress, Link } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as categoriesApi from '@/api/activityCategories';
import type { ActivityCategoryRow } from '@/api/activityCategories';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { activitiesHref } from '@/utils/gridLinks';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { ActivityCategoryCreateDialog } from './ActivityCategoryCreateDialog';


const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);
const DEFAULT_ORDER = ['id', 'tenant', 'title', 'icon', 'activity_count'];

export function ActivityCategoriesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_activities_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'activity_categories',
    defaultOrder: DEFAULT_ORDER,
    defaultOrdering: 'title',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what the create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());

  const columns = useMemo<GridColDef<ActivityCategoryRow>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink
            to={`/activity-categories/${p.row.id}`}
            style={{ color: '#0a1a3f', fontWeight: 500 }}
          >
            {p.row.title}
          </RouterLink>
        ),
      },
      {
        field: 'icon',
        headerName: 'Icon',
        width: 90,
        sortable: false,
        renderCell: (p) =>
          p.row.icon ? (
            <ImageIcon fontSize="small" color="action" titleAccess="Has icon" />
          ) : (
            <ImageNotSupportedIcon fontSize="small" color="disabled" titleAccess="No icon" />
          ),
      },
      {
        field: 'activity_count',
        headerName: 'Activities',
        width: 100,
        // Counted for the page's rows only (Appendix A 3g): the backend cannot order by it.
        sortable: false,
        valueGetter: (_, row) => row.activity_count,
        // The count opens the activities it counts; a zero has nothing to open.
        renderCell: (p) =>
          p.row.activity_count > 0 ? (
            <Link
              component={RouterLink}
              to={activitiesHref([], p.row.tenant?.id, { category: p.row.id })}
              aria-label={`${p.row.activity_count} ${p.row.activity_count === 1 ? 'activity' : 'activities'} in ${p.row.title}`}
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.activity_count}
            </Link>
          ) : (
            p.row.activity_count
          ),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      categoriesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'title',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="activities"
        title="Activity Categories"
        subtitle="Cross-tenant activity categories."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New category
            </Button>
          )
        }
      />

      <ActivityCategoryCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<ActivityCategoryRow>
          queryKey={['activity_categories']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="activity_categories"
          batchActions={[
            deleteBatchAction('console_activities_manage'),
            batchEditAction('activity_categories', 'category'),
          ]}
          defaultSort={{ field: 'title', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Title',
            exportFileName: 'activity-categories',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
